import type {PayrollFrequency, RfcMecForm, RfcSection125Form} from '@common/lib/rfcSchema';
import type {WizardState} from './types';

export const WIZARD_STEPS = [
  {key: 'agent', label: 'Agent Lookup'},
  {key: 'group-info', label: 'Group Info'},
  {key: 'plans', label: 'Products'},
  {key: 'review', label: 'Review & Submit'},
] as const;

export const WIZARD_STEP_LABELS = WIZARD_STEPS.map((s) => s.label);

export type BillingCycleId = 'BW' | 'MO' | 'SM' | 'WE' | 'WK';

const PAYROLL_FREQUENCY_TO_BILLING_CYCLE: Record<PayrollFrequency, BillingCycleId> = {
  BI_MONTHLY: 'SM',
  BI_WEEKLY: 'BW',
  MONTHLY: 'MO',
  SEMI_MONTHLY: 'SM',
  WEEKLY: 'WK',
  WEEKLY_48: 'WE',
  WEEKLY_52: 'WK',
};

export function payrollFrequencyToBillingCycle(freq: string | undefined): BillingCycleId {
  if (freq && freq in PAYROLL_FREQUENCY_TO_BILLING_CYCLE) {
    return PAYROLL_FREQUENCY_TO_BILLING_CYCLE[freq as PayrollFrequency];
  }
  return 'MO';
}

const BILLING_CYCLE_LABELS: Record<BillingCycleId, string> = {
  BW: 'Bi-Weekly',
  MO: 'Monthly',
  SM: 'Semi-Monthly',
  WE: 'Weekly (48)',
  WK: 'Weekly',
};

export function billingCycleLabel(id: string): string {
  if (id in BILLING_CYCLE_LABELS) {
    return BILLING_CYCLE_LABELS[id as BillingCycleId];
  }
  return id;
}

/**
 * The RFC form holding the group's answers. Every RFC stores both a Section 125
 * and a MEC form (the unused one stays blank), so pick by the enabled flag
 * rather than by which column is non-null. Section 125 wins if both are on.
 */
export function enabledRfcForm(
  rfc:
    | {
        mecFormData: unknown;
        mecFormEnabled: boolean;
        section125FormData: unknown;
        section125FormEnabled: boolean;
      }
    | null
    | undefined,
): RfcSection125Form | RfcMecForm | null {
  if (rfc?.section125FormEnabled) return (rfc.section125FormData ?? null) as RfcSection125Form;
  if (rfc?.mecFormEnabled) return (rfc.mecFormData ?? null) as RfcMecForm;
  return null;
}

/**
 * Wizard fields pre-filled from an RFC's form data (Section 125 or MEC; both
 * share these sections). Stored drafts can be partial, so every read is
 * optional-chained and empty values are skipped. FEIN and address live per
 * location on the RFC; the first location is the group's primary.
 */
export function wizardPrefillFromRfc(
  formData: RfcSection125Form | RfcMecForm | null,
): Partial<WizardState> {
  const updates: Partial<WizardState> = {};
  if (!formData) return updates;

  const companyName = formData.groupInfo?.companyName;
  const primaryLocation = formData.groupInfo?.locations?.[0];
  const contact = formData.contacts?.authorizedSigner;

  if (companyName) updates.groupName = companyName;
  if (primaryLocation?.fein) updates.federalTaxId = primaryLocation.fein;

  if (primaryLocation?.companyAddress) {
    const {city, state, street, zip} = primaryLocation.companyAddress;
    if (street) updates.addressLine1 = street;
    if (city) updates.city = city;
    if (state) updates.stateCode = state;
    if (zip) updates.zipCode = zip;
  }

  if (contact) {
    if (contact.firstName) updates.firstName = contact.firstName;
    if (contact.lastName) updates.lastName = contact.lastName;
    if (contact.phone) updates.phone = contact.phone;
    if (contact.email) updates.email = contact.email;
  }

  const freqs = formData.admin?.payrollFrequencies;
  if (freqs && freqs.length > 0) {
    updates.billingCycleId = payrollFrequencyToBillingCycle(freqs[0]);
  }

  return updates;
}
